import type { ReactNode } from 'react';
import { DRAW_TOOL_LABEL, type DrawTool, type ShapeKind } from '../doc/schema';
import {
  IconArrow,
  IconBrush,
  IconEllipse,
  IconEraser,
  IconFill,
  IconFineliner,
  IconLasso,
  IconLine,
  IconMarker,
  IconPen,
  IconPicker,
  IconRect,
} from '../ui/icons';
import { FEATHERS, type CutoutMode } from './cutout';
import { ColorPicker, OpacitySlider, SizePicker } from '../ui/ColorPicker';
import { usePane } from './context';
import { HandleControls, useFloatingCard } from './floating';
import { isInkMode, setInkStyle, setPenMode, setToolSettings, useToolSettings, type PenMode } from './toolSettings';

const SHAPES: { kind: ShapeKind; label: string; icon: () => React.JSX.Element }[] = [
  { kind: 'rect', label: 'Rectangle', icon: IconRect },
  { kind: 'ellipse', label: 'Ellipse', icon: IconEllipse },
  { kind: 'line', label: 'Line', icon: IconLine },
  { kind: 'arrow', label: 'Arrow', icon: IconArrow },
];

type ModeButton = { mode: PenMode; label: string; icon: () => React.JSX.Element };

const INK_TOOLS: ModeButton[] = [
  { mode: 'pen', label: 'Pen: pressure sets the width (P)', icon: IconPen },
  { mode: 'fineliner', label: 'Fineliner: always the same width (U)', icon: IconFineliner },
  { mode: 'brush', label: 'Brush pen: strong pressure, pointed ends (R)', icon: IconBrush },
  { mode: 'highlighter', label: 'Highlighter (M)', icon: IconMarker },
  { mode: 'fill', label: 'Fill: paints the inside of the loop you draw (G)', icon: IconFill },
];

const INK_HELPERS: ModeButton[] = [
  { mode: 'eraser', label: 'Eraser (E)', icon: IconEraser },
  { mode: 'lasso', label: 'Lasso: select strokes (L)', icon: IconLasso },
  { mode: 'picker', label: 'Colour picker: take a colour from the board (or hold Alt while drawing)', icon: IconPicker },
];

/** Tip sizes offered per tool, in screen pixels. Fills have none. */
const INK_SIZES: Record<DrawTool, number[]> = {
  pen: [2, 4, 8, 14, 24],
  fineliner: [1, 2, 3, 5, 8, 12],
  brush: [4, 8, 14, 24, 40],
  highlighter: [12, 24, 40],
  fill: [],
};

const CUT_MODES: { mode: CutoutMode; label: string; icon: () => React.JSX.Element }[] = [
  { mode: 'lasso', label: 'Lasso (freehand outline)', icon: IconLasso },
  { mode: 'rect', label: 'Rectangle', icon: IconRect },
  { mode: 'ellipse', label: 'Ellipse', icon: IconEllipse },
];

/** Options card next to the toolbar for tools that have settings (shape, draw). */
export function ToolOptions() {
  const tool = usePane((s) => s.tool);
  if (tool === 'shape')
    return (
      <OptionsPanel title="Shape">
        <ShapeOptions />
      </OptionsPanel>
    );
  if (tool === 'draw')
    return (
      <OptionsPanel title="Draw">
        <DrawOptions />
      </OptionsPanel>
    );
  if (tool === 'cutout')
    return (
      <OptionsPanel title="Cut out">
        <CutoutOptions />
      </OptionsPanel>
    );
  return null;
}

/**
 * The card itself. Docked beside the toolbar by default; dragging its title bar detaches it so
 * it floats anywhere in the pane (the position is remembered per device, shared by all tools).
 * The dock button, or a double-click on the bar, puts it back.
 */
function OptionsPanel({ title, children }: { title: string; children: ReactNode }) {
  const card = useFloatingCard<HTMLDivElement>('tool-options');
  return (
    <div ref={card.ref} className={`tool-options${card.floating ? ' is-floating' : ''}`} style={card.style} onPointerDown={(e) => e.stopPropagation()}>
      <div className="opt-handle" title="Drag to move these options anywhere (double-click docks them back)" {...card.handleProps}>
        <span className="opt-title">{title}</span>
        <HandleControls card={card} />
      </div>
      <div className="opt-body">{children}</div>
    </div>
  );
}

function ShapeOptions() {
  const s = useToolSettings((t) => t);
  return (
    <>
      <div className="opt-row">
        {SHAPES.map(({ kind, label, icon: Icon }) => (
          <button
            key={kind}
            type="button"
            className={`tb-btn${s.shapeKind === kind ? ' is-active' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => setToolSettings({ shapeKind: kind })}
          >
            <Icon />
          </button>
        ))}
      </div>
      <div className="opt-label">Fill</div>
      <ColorPicker value={s.fill} onChange={(fill) => setToolSettings({ fill })} allowNone label="Fill" />
      <div className="opt-label">Stroke</div>
      <ColorPicker value={s.stroke} onChange={(stroke) => setToolSettings({ stroke })} allowNone label="Stroke" />
      <SizePicker value={s.strokeWidth} options={[1, 2, 3, 6, 10]} onChange={(strokeWidth) => setToolSettings({ strokeWidth })} />
    </>
  );
}

function ModeRow({ modes, current }: { modes: ModeButton[]; current: PenMode }) {
  return (
    <div className="opt-row">
      {modes.map(({ mode, label, icon: Icon }) => (
        <button
          key={mode}
          type="button"
          className={`tb-btn${current === mode ? ' is-active' : ''}`}
          title={label}
          aria-label={label}
          aria-pressed={current === mode}
          onClick={() => setPenMode(mode)}
        >
          <Icon />
        </button>
      ))}
    </div>
  );
}

function DrawOptions() {
  const s = useToolSettings((t) => t);
  const ink = isInkMode(s.penMode) ? s.penMode : null;
  const target = DRAW_TOOL_LABEL[s.inkTool].toLowerCase();
  return (
    <>
      <ModeRow modes={INK_TOOLS} current={s.penMode} />
      <ModeRow modes={INK_HELPERS} current={s.penMode} />
      {ink && (
        <>
          <ColorPicker value={s.ink[ink].color} onChange={(color) => setInkStyle(ink, { color })} label={`${DRAW_TOOL_LABEL[ink]} colour`} />
          {INK_SIZES[ink].length > 0 && (
            <SizePicker value={s.ink[ink].size} options={INK_SIZES[ink]} onChange={(size) => setInkStyle(ink, { size })} />
          )}
          <OpacitySlider value={s.ink[ink].opacity} onChange={(opacity) => setInkStyle(ink, { opacity })} />
        </>
      )}
      {ink === 'fill' && <div className="opt-hint">Draw around an area to paint it. Fills sit on a layer of their own, under the lines.</div>}
      {ink && ink !== 'fill' && (
        <label
          className="opt-check"
          title="Keep the pen still for a moment at the end of a line and it becomes straight; keep moving to aim it. Shift draws straight lines in 15 degree steps"
        >
          <input type="checkbox" checked={s.holdStraighten} onChange={(e) => setToolSettings({ holdStraighten: e.target.checked })} />
          Pause to straighten lines
        </label>
      )}
      {s.penMode === 'eraser' && (
        <>
          <SizePicker value={s.eraserSize} options={[12, 24, 48]} onChange={(eraserSize) => setToolSettings({ eraserSize })} />
          <div className="opt-seg" role="group" aria-label="What the eraser removes">
            <button type="button" className={s.eraserWhole ? '' : 'is-active'} aria-pressed={!s.eraserWhole} onClick={() => setToolSettings({ eraserWhole: false })}>
              Partial
            </button>
            <button type="button" className={s.eraserWhole ? 'is-active' : ''} aria-pressed={s.eraserWhole} onClick={() => setToolSettings({ eraserWhole: true })}>
              Whole strokes
            </button>
          </div>
          <div className="opt-hint">
            {s.eraserWhole ? 'Removes every stroke it touches.' : 'Rubs out only what it covers: lines split in two, fills get a hole.'}
          </div>
        </>
      )}
      {s.penMode === 'lasso' && (
        <div className="opt-hint">Draw a loop around strokes to select them (Shift adds). Tap one to pick it. Drag a selected stroke to move; Delete removes.</div>
      )}
      {s.penMode === 'picker' && (
        <>
          <div className="opt-label">Colour for the {target}</div>
          <ColorPicker
            value={s.ink[s.inkTool].color}
            onChange={(color) => setInkStyle(s.inkTool, { color })}
            label={`${DRAW_TOOL_LABEL[s.inkTool]} colour`}
          />
          <div className="opt-hint">
            Press on the board and move to preview; let go to take the colour and return to the {target}. Holding Alt picks a colour without
            leaving the pen.
          </div>
        </>
      )}
      <label className="opt-check" title="While drawing, a single finger never draws or pans; two fingers still pan and zoom. Other tools are not affected">
        <input type="checkbox" checked={s.penOnly} onChange={(e) => setToolSettings({ penOnly: e.target.checked })} />
        Pen only (fingers don't draw)
      </label>
    </>
  );
}

function CutoutOptions() {
  const s = useToolSettings((t) => t);
  return (
    <>
      <div className="opt-row">
        {CUT_MODES.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            className={`tb-btn${s.cutoutMode === mode ? ' is-active' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => setToolSettings({ cutoutMode: mode })}
          >
            <Icon />
          </button>
        ))}
      </div>
      <div className="opt-label">Soft edge</div>
      <SizePicker value={s.cutoutFeather} options={FEATHERS} onChange={(cutoutFeather) => setToolSettings({ cutoutFeather })} />
      <label className="opt-check" title="Also remove the piece from the image it was cut from (a locked image is left whole)">
        <input type="checkbox" checked={s.cutoutPunch} onChange={(e) => setToolSettings({ cutoutPunch: e.target.checked })} />
        Leave a hole in the source
      </label>
      <div className="opt-hint">
        Draw around part of an image. The piece becomes its own image on top of the original, selected, so it can be dragged away. Where
        images overlap, select the one to cut from first.
      </div>
    </>
  );
}
